import {createContext, useCallback, useContext, useMemo, useState} from "react";

const EMPTY_STATUS = {projectId: null, pending: false, integrations: null};
const NOOP = () => {};

export const ProjectIntegrationStatusContext = createContext({
    status: EMPTY_STATUS,
    publish: NOOP,
});

export function projectIntegrationStatusProjection(integrations) {
    if (!Array.isArray(integrations)) return null;

    return integrations.flatMap((integration) => {
        if (typeof integration?.id !== "string" || !integration.id
            || typeof integration?.name !== "string" || !integration.name) {
            return [];
        }

        return [{
            id: integration.id,
            name: integration.name,
            authorizationSuccessful: typeof integration.authorizationSuccessful === "boolean" ? integration.authorizationSuccessful : null,
            paused: typeof integration.paused === "boolean" ? integration.paused : null,
        }];
    });
}

export function projectIntegrationStatusForProject(status, projectId) {
    if (!projectId) return EMPTY_STATUS;
    if (status?.projectId !== projectId) return {projectId, pending: true, integrations: null};
    return status;
}

export function ProjectIntegrationStatusProvider({children}) {
    const [status, setStatus] = useState(EMPTY_STATUS);
    const publish = useCallback((nextStatus) => setStatus(nextStatus ?? EMPTY_STATUS), []);
    const value = useMemo(() => ({status, publish}), [status, publish]);

    return <ProjectIntegrationStatusContext.Provider value={value}>{children}</ProjectIntegrationStatusContext.Provider>;
}

export function useProjectIntegrationStatus(projectId) {
    const {status} = useContext(ProjectIntegrationStatusContext);
    return projectIntegrationStatusForProject(status, projectId);
}

export function usePublishProjectIntegrationStatus() {
    return useContext(ProjectIntegrationStatusContext).publish;
}
